import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface CariAltKategori {
    cariAltKategoriID: number;
    turu: string;
    altKategoriAdi: string;
    aktif: boolean;
}

interface CariAltKategoriForm {
    turu: string;
    altKategoriAdi: string;
    aktif: boolean;
}

const CariAltKategorileri: React.FC = () => {
    const { t } = useTranslation();
    const [kategoriler, setKategoriler] = useState<CariAltKategori[]>([]);
    const [selectedKategori, setSelectedKategori] = useState<CariAltKategori | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<CariAltKategoriForm>({ turu: 'Cari', altKategoriAdi: '', aktif: true });
    const [filterTuru, setFilterTuru] = useState<string>('');

    const fetchKategoriler = async () => {
        try {
            const response = await axios.get('/api/CariAltKategorileri');
            setKategoriler(response.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    useEffect(() => { fetchKategoriler(); }, []);

    const filteredKategoriler = filterTuru
        ? kategoriler.filter(k => k.turu === filterTuru)
        : kategoriler;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (selectedKategori) {
                await axios.put(`/api/CariAltKategorileri/${selectedKategori.cariAltKategoriID}`, formData);
                toast.success(t('msg.islemBasarili'));
            } else {
                await axios.post('/api/CariAltKategorileri', formData);
                toast.success(t('msg.islemBasarili'));
            }
            fetchKategoriler();
            handleCloseModal();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (kategori: CariAltKategori) => {
        setSelectedKategori(kategori);
        setFormData({ turu: kategori.turu, altKategoriAdi: kategori.altKategoriAdi, aktif: kategori.aktif });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedKategori(null);
        setFormData({ turu: 'Cari', altKategoriAdi: '', aktif: true });
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                .cak-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 28px 32px;
                    background: #f4f6fb;
                    min-height: 100vh;
                }

                .cak-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                }
                .cak-header-left { display: flex; flex-direction: column; gap: 3px; }
                .cak-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #0f172a;
                    letter-spacing: -0.4px;
                    margin: 0;
                }
                .cak-subtitle { font-size: 13px; color: #94a3b8; font-weight: 400; }
                .cak-header-actions { display: flex; align-items: center; gap: 12px; }
                .cak-filter-select {
                    padding: 9px 14px;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 13px;
                    font-family: inherit;
                    color: #334155;
                    background: #fff;
                    outline: none;
                    cursor: pointer;
                    transition: border-color 0.15s;
                }
                .cak-filter-select:focus { border-color: #cd6906; }
                .cak-add-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #cd6906;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: inherit;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.18s, box-shadow 0.18s, transform 0.12s;
                }
                .cak-add-btn:hover {
                    background: #b85d05;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transform: translateY(-1px);
                }
                .cak-add-btn svg { width: 16px; height: 16px; stroke: #fff; stroke-width: 2.5; fill: none; }

                .cak-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    overflow: hidden;
                }

                .cak-table { width: 100%; border-collapse: collapse; }
                .cak-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .cak-table thead th {
                    padding: 13px 20px;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #94a3b8;
                    text-align: left;
                    white-space: nowrap;
                }
                .cak-table thead th.cak-th-actions { text-align: right; }
                .cak-table tbody tr {
                    border-bottom: 1px solid #f8fafc;
                    transition: background 0.13s;
                }
                .cak-table tbody tr:last-child { border-bottom: none; }
                .cak-table tbody tr.cak-row-hovered { background: #f0f5ff; }
                .cak-table tbody td {
                    padding: 15px 20px;
                    font-size: 14px;
                    color: #1e293b;
                    vertical-align: middle;
                }

                .cak-cell-id { font-size: 12px; color: #94a3b8; font-weight: 500; width: 56px; }
                .cak-cell-name { font-weight: 600; color: #0f172a; font-size: 14px; }
                .cak-cell-turu { width: 120px; }
                .cak-cell-durum { width: 110px; }
                .cak-cell-actions { width: 90px; text-align: right; }

                .cak-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 11px 4px 8px;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 600;
                }
                .cak-pill-aktif { background: #dcfce7; color: #15803d; }
                .cak-pill-pasif { background: #fee2e2; color: #b91c1c; }
                .cak-pill-cari { background: #dbeafe; color: #1d4ed8; }
                .cak-pill-musteri { background: #ede9fe; color: #6d28d9; }
                .cak-pill-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
                .cak-pill-aktif .cak-pill-dot { background: #22c55e; }
                .cak-pill-pasif .cak-pill-dot { background: #ef4444; }

                .cak-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 6px;
                    opacity: 0;
                    transition: opacity 0.15s;
                }
                .cak-row-hovered .cak-actions { opacity: 1; }
                .cak-ghost-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px; height: 34px;
                    border-radius: 8px;
                    border: 1.5px solid transparent;
                    background: transparent;
                    cursor: pointer;
                    transition: background 0.15s, border-color 0.15s;
                    color: #64748b;
                }
                .cak-ghost-edit:hover { background: #fff8f0; border-color: #f5d5a0; color: #cd6906; }
                .cak-ghost-btn svg {
                    width: 15px; height: 15px;
                    stroke: currentColor; stroke-width: 2;
                    fill: none; stroke-linecap: round; stroke-linejoin: round;
                }

                .cak-footer {
                    padding: 13px 20px;
                    border-top: 1.5px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .cak-count { font-size: 13px; color: #94a3b8; font-weight: 500; }
                .cak-count span { color: #475569; font-weight: 600; }

                .cak-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .cak-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.5; }
                .cak-empty-text { font-size: 15px; font-weight: 500; }

                .cak
                .cak-modal {
                    background: #fff;
                    border-radius: 16px 0 0 16px;
                    width: 480px; max-width: 92vw;
                    height: 100%;
                    box-shadow: -8px 0 40px rgba(0,0,0,0.12);
                    animation: cakDrawerIn 0.28s cubic-bezier(0.32,0.72,0,1);
                    display: flex; flex-direction: column;
                    overflow: hidden;
                }
                @keyframes cakDrawerIn {
                    from { transform: translateX(100%); }
                    to   { transform: translateX(0); }
                }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .cak-modal-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f3f4f6;
                    flex-shrink: 0;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .cak-modal-title { font-size: 18px; font-weight: 600; color: #0f172a; margin: 0; }
                .cak-modal-close {
                    width: 32px; height: 32px; border-radius: 8px; border: none;
                    background: none; color: #9ca3af; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .cak-modal-close:hover { background: #f3f4f6; color: #374151; }
                .cak-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 20px; flex: 1; overflow-y: auto; }
                .cak-field label {
                    display: block;
                    font-size: 12px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.07em;
                    color: #64748b; margin-bottom: 6px;
                }
                .cak-field input[type="text"],
                .cak-field select {
                    width: 100%; padding: 10px 14px;
                    border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box;
                    background: #fff;
                }
                .cak-field input:focus,
                .cak-field select:focus {
                    border-color: #cd6906;
                    box-shadow: 0 0 0 3px rgba(205,105,6,0.12);
                }
                .cak-toggle-row {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 12px 14px;
                    background: #f8fafc; border-radius: 10px; border: 1.5px solid #e2e8f0;
                }
                .cak-toggle-label { font-size: 14px; font-weight: 500; color: #334155; }
                .cak-switch { position: relative; width: 42px; height: 24px; flex-shrink: 0; }
                .cak-switch input { opacity: 0; width: 0; height: 0; }
                .cak-switch-track {
                    position: absolute; inset: 0;
                    border-radius: 999px; background: #cbd5e1;
                    cursor: pointer; transition: background 0.2s;
                }
                .cak-switch input:checked + .cak-switch-track { background: #22c55e; }
                .cak-switch-track::after {
                    content: ''; position: absolute;
                    top: 3px; left: 3px; width: 18px; height: 18px;
                    border-radius: 50%; background: #fff;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.18);
                    transition: transform 0.2s;
                }
                .cak-switch input:checked + .cak-switch-track::after { transform: translateX(18px); }
                .cak-modal-footer {
                    padding: 16px 24px; border-top: 1px solid #f3f4f6;
                    background: rgba(255,255,255,0.9); backdrop-filter: blur(8px); flex-shrink: 0;
                    display: flex; justify-content: flex-end; gap: 10px;
                }
                .cak-btn-cancel {
                    padding: 9px 18px; border-radius: 8px; border: none;
                    background: none; color: #4b5563;
                    font-size: 13px; font-weight: 500; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .cak-btn-cancel:hover { background: #f9fafb; }
                .cak-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .cak-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .cak-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .cak-spinner {
                    width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: cakSpin 0.7s linear infinite;
                }
                @keyframes cakSpin { to { transform: rotate(360deg); } }
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: '24px', background: '#fff' }}>
                {/* Header */}
                <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#111827', margin: 0, lineHeight: 1.3 }}>Cari Alt Kategorileri</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>Cari alt kategorileri yönetin</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <select
                            value={filterTuru}
                            onChange={e => setFilterTuru(e.target.value)}
                            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', color: '#374151', background: '#fff', outline: 'none', cursor: 'pointer' }}
                        >
                            <option value="">{t('label.tumu')}</option>
                            <option value="Cari">Cari</option>
                            <option value="Müşteri">Müşteri</option>
                        </select>
                        <button
                            onClick={() => setShowModal(true)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#a85505'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#cd6906'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                        >
                            <svg style={{ width: 14, height: 14, stroke: '#fff', strokeWidth: 2.5, fill: 'none' }} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            Yeni Kategori
                        </button>
                    </div>
                </div>

                {/* Card List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {filteredKategoriler.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#9ca3af', fontSize: 14 }}>
                            <div style={{ fontSize: 32, marginBottom: 10 }}>📂</div>
                            <div>{t('msg.henuzKayitEklenmedi')}</div>
                        </div>
                    ) : (
                        filteredKategoriler.map((kategori) => (
                            <div
                                key={kategori.cariAltKategoriID}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderRadius: 10, border: '1px solid #f3f4f6', background: '#fff', transition: 'all 0.15s', cursor: 'default' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#fef9f4'; e.currentTarget.style.borderColor = '#fde3c1'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(205,105,6,0.08)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#f3f4f6'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: 9, background: '#fef3e0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <svg style={{ width: 18, height: 18, stroke: '#cd6906', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 500, fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kategori.altKategoriAdi}</div>
                                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>#{kategori.cariAltKategoriID}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: kategori.turu === 'Cari' ? '#e0f2fe' : '#f3e8ff', color: kategori.turu === 'Cari' ? '#0369a1' : '#7e22ce' }}>
                                        {kategori.turu}
                                    </span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: kategori.aktif ? '#dcfce7' : '#f3f4f6', color: kategori.aktif ? '#166534' : '#6b7280' }}>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: kategori.aktif ? '#22c55e' : '#9ca3af', display: 'inline-block' }} />
                                        {kategori.aktif ? 'Aktif' : 'Pasif'}
                                    </span>
                                    <button
                                        title={t('btn.duzenle')}
                                        onClick={() => handleEdit(kategori)}
                                        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                                    >
                                        <svg style={{ width: 14, height: 14, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                    {filteredKategoriler.length > 0 && (
                        <div style={{ padding: '10px 4px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', color: '#6b7280', fontSize: 12 }}>
                            <span>Toplam <b style={{ color: '#111827' }}>{filteredKategoriler.length}</b> kategori</span>
                            <span>Aktif: <b style={{ color: '#166534' }}>{filteredKategoriler.filter(k => k.aktif).length}</b> &nbsp; Pasif: <b style={{ color: '#6b7280' }}>{filteredKategoriler.filter(k => !k.aktif).length}</b></span>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="cak-overlay" onClick={handleCloseModal}>
                    <div className="cak-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{selectedKategori ? 'Kategori Düzenle' : 'Yeni Kategori Ekle'}</h2>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: 8, color: '#9ca3af', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }} onClick={handleCloseModal}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="cak-modal-body">
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Türü</label>
                                    <select
                                        id="turuSelect"
                                        value={formData.turu}
                                        onChange={e => setFormData({ ...formData, turu: e.target.value })}
                                        required
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, appearance: 'auto', transition: 'border-color 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    >
                                        <option value="Cari">Cari</option>
                                        <option value="Müşteri">Müşteri</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }} htmlFor="altKategoriAdiInput">Alt Kategori Adı</label>
                                    <input
                                        type="text"
                                        id="altKategoriAdiInput"
                                        placeholder={t('ayarlar.cariAltKategori.placeholder')}
                                        value={formData.altKategoriAdi}
                                        onChange={e => setFormData({ ...formData, altKategoriAdi: e.target.value })}
                                        required
                                        maxLength={100}
                                        autoFocus
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    />
                                </div>
                                <div className="cak-toggle-row">
                                    <span className="cak-toggle-label">Durum — Aktif</span>
                                    <label className="cak-switch">
                                        <input
                                            type="checkbox"
                                            checked={formData.aktif}
                                            onChange={e => setFormData({ ...formData, aktif: e.target.checked })}
                                        />
                                        <span className="cak-switch-track" />
                                    </label>
                                </div>
                            </div>
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    onClick={handleCloseModal}
                                >{t('btn.iptal')}</button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: saving ? '#e5b678' : '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: saving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: saving ? 'none' : '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s' }}
                                    onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; } }}
                                    onMouseLeave={e => { if (!saving) { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; } }}
                                >
                                    {saving && <span className="cak-spinner" />}
                                    {selectedKategori ? t('btn.kaydet') : t('btn.ekle')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default CariAltKategorileri;
